import { useEffect, useState } from "react";
import { Bell, BellOff, Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useServerFn } from "@tanstack/react-start";
import { subscribePush, unsubscribePush } from "@/lib/push.functions";
import {
  subscribeToPushDetailed,
  unsubscribeFromPush,
  serializeSubscription,
  getExistingSubscription,
} from "@/lib/push-client";

type Metal = "gold" | "silver";

const FIXED_THRESHOLDS: Record<string, number> = {
  pakistan: 3000,
  india: 3000,
  uae: 30,
  saudi: 30,
};

function alertCopy(country: string, metal: Metal, currency: string) {
  const amount = (FIXED_THRESHOLDS[country] ?? 3000).toLocaleString();
  const metalName = metal === "gold" ? "gold" : "silver";
  if (country === "pakistan") {
    return {
      dir: "rtl" as const,
      title: "روزانہ ریٹ الرٹ حاصل کریں",
      body: `اگر آپ ${metalName === "gold" ? "سونے" : "چاندی"} کے ریٹ سے روزانہ اپ ڈیٹ رہنا چاہتے ہیں تو سبسکرائب کریں۔ قیمت ${currency} ${amount} اوپر یا نیچے ہوتے ہی آپ کو Chrome notification مل جائے گا۔`,
      note: "No email required — browser se direct alert aayega.",
    };
  }
  if (country === "india") {
    return {
      dir: "ltr" as const,
      title: "रोज़ाना रेट अलर्ट पाएं",
      body: `अगर आप ${metalName === "gold" ? "सोने" : "चांदी"} के रेट से updated रहना चाहते हैं, तो subscribe करें। कीमत ${currency} ${amount} ऊपर या नीचे जाते ही Chrome notification आ जाएगा।`,
      note: "No email required — alert direct browser par aayega.",
    };
  }
  if (country === "uae" || country === "saudi") {
    return {
      dir: "rtl" as const,
      title: "اشترك في تنبيهات السعر اليومية",
      body: `إذا كنت تريد متابعة سعر ${metalName === "gold" ? "الذهب" : "الفضة"} يومياً، اشترك الآن. عند تحرك السعر بمقدار ${amount} ${currency} صعوداً أو هبوطاً سيصلك تنبيه مباشر من Chrome.`,
      note: "No email required — يمكنك الإلغاء في أي وقت.",
    };
  }
  return {
    dir: "ltr" as const,
    title: "Get daily rate alerts",
    body: `Subscribe and get a Chrome notification when ${metalName} moves by ${currency} ${amount} up or down.`,
    note: "No email required — unsubscribe anytime.",
  };
}

export function PriceAlertBell({
  country,
  metal,
  currency = "PKR",
  className = "",
}: {
  country: string;
  metal: Metal;
  currency?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const threshold = FIXED_THRESHOLDS[country] ?? 3000;
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sub = useServerFn(subscribePush);
  const unsub = useServerFn(unsubscribePush);
  const copy = alertCopy(country, metal, currency);

  useEffect(() => {
    getExistingSubscription().then((s) => setSubscribed(!!s));
  }, []);

  async function handleSubscribe() {
    setBusy(true); setError(null);
    try {
      const res = await subscribeToPushDetailed();
      if (!res.ok) {
        if (res.endpoint) await unsub({ data: { endpoint: res.endpoint } });
        setSubscribed(false);
        setError(res.reason);
        return;
      }
      const s = serializeSubscription(res.sub);
      const r = await sub({ data: { ...s, country, metal, threshold_pkr: threshold, city_slug: null } });
      if (!r.ok) { setError("Server par save nahi ho paya, dobara try karein."); return; }
      setSubscribed(true); setDone(true);
      setTimeout(() => { setDone(false); setOpen(false); }, 1400);
    } catch (e) {
      setError((e as Error).message);
    } finally { setBusy(false); }
  }

  async function handleUnsubscribe() {
    setBusy(true);
    try {
      const endpoint = await unsubscribeFromPush();
      if (endpoint) await unsub({ data: { endpoint } });
      setSubscribed(false);
      setOpen(false);
    } finally { setBusy(false); }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={subscribed ? "Manage price alerts" : "Get price alerts"}
        className={`inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/60 px-3 py-1.5 text-xs font-semibold text-foreground/90 hover:bg-card transition-colors ${className}`}
      >
        {subscribed ? <Bell className="h-3.5 w-3.5 text-primary" /> : <Bell className="h-3.5 w-3.5" />}
        {subscribed ? "Alerts On" : "Price Alert"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader dir={copy.dir} className={copy.dir === "rtl" ? "text-right" : undefined}>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-primary" />
              {subscribed ? "Manage price alerts" : copy.title}
            </DialogTitle>
            <DialogDescription>
              {copy.body}
            </DialogDescription>
          </DialogHeader>
          {done ? (
            <div className="py-8 text-center">
              <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400">
                <Check className="h-6 w-6" />
              </div>
              <div className="font-semibold">You're subscribed!</div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm font-semibold text-primary">
                Fixed alert: {currency} {threshold.toLocaleString()} price move
              </div>
              {error && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300 leading-relaxed">
                  {error}
                  {error.includes("Block state") && (
                    <div className="mt-2 text-[11px] text-red-200/90">
                      Chrome setting change karne ke baad page reload zaroor karein — tabhi browser naya Allow/Deny prompt ya active subscription dega.
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {!subscribed ? (
                  <Button onClick={handleSubscribe} disabled={busy} className="flex-1">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4 mr-1" />}
                    Subscribe
                  </Button>
                ) : (
                  <>
                    <Button onClick={handleSubscribe} disabled={busy} className="flex-1">Update</Button>
                    <Button variant="outline" onClick={handleUnsubscribe} disabled={busy}>
                      <BellOff className="h-4 w-4 mr-1" /> Turn off
                    </Button>
                  </>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground">
                {copy.note}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}